import type React from "react";

interface ActiveFiltersProps {
	activeFilters: string[];
	disabledFilters: Set<string>;
	getFilterDescription: (filterName: string) => string;
	toggleFilter: (filterName: string) => void;
	removeFilter: (filterName: string) => void;
	currentTool: string | null;
}

export const ActiveFilters: React.FC<ActiveFiltersProps> = ({
	activeFilters,
	disabledFilters,
	getFilterDescription,
	toggleFilter,
	removeFilter,
	currentTool,
}) => {
	return (
		<div className="flex flex-col h-full">
			<h3 className="font-semibold text-sm text-foreground mb-2 shrink-0">
				Active Filters
			</h3>
			{activeFilters.length > 0 ? (
				<div className="flex-1 overflow-y-auto space-y-1.5 pr-1">
					{activeFilters.map((filterName) => {
						const isDisabled = disabledFilters.has(filterName);
						return (
							<div
								key={filterName}
								className={`flex items-center gap-2 p-2 rounded-lg border text-xs transition ${
									isDisabled
										? "bg-muted border-border opacity-60"
										: "bg-card border-border"
								}`}
							>
								<span className="font-medium text-foreground truncate flex-1 min-w-0">
									{getFilterDescription(filterName)}
								</span>
								<button
									onClick={() => toggleFilter(filterName)}
									className={`p-1 rounded-md shrink-0 transition ${
										isDisabled
											? "text-dim hover:text-foreground hover:bg-muted"
											: "text-accent-indigo hover:bg-accent-indigo/10"
									}`}
									title={isDisabled ? "Enable filter" : "Disable filter"}
								>
									{isDisabled ? (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth="1.5"
											stroke="currentColor"
											className="w-4 h-4"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88"
											/>
										</svg>
									) : (
										<svg
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											strokeWidth="1.5"
											stroke="currentColor"
											className="w-4 h-4"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.574 3.01 9.963 7.183.07.218.07.45 0 .669C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.574-3.01-9.963-7.183z"
											/>
											<ellipse cx="12" cy="12" rx="3" ry="3" />
										</svg>
									)}
								</button>
								<button
									onClick={() => removeFilter(filterName)}
									className="p-1 rounded-md shrink-0 text-dim hover:text-destructive hover:bg-destructive/10 transition"
									title="Remove filter"
								>
									<svg
										xmlns="http://www.w3.org/2000/svg"
										fill="none"
										viewBox="0 0 24 24"
										strokeWidth="1.5"
										stroke="currentColor"
										className="w-4 h-4"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M6 18 18 6M6 6l12 12"
										/>
									</svg>
								</button>
							</div>
						);
					})}
				</div>
			) : (
				<div className="text-center py-2 text-dim">
					<p className="text-xs">No active filters</p>
					<p className="text-xs mt-1">Use the tools to add filters</p>
				</div>
			)}
		</div>
	);
};
